import primaryComponents from "../../primaryComponents";
import secondaryComponents from "../../secondaryComponents";
import styles from "./ReturnStockWidget.module.scss";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Controller } from "react-hook-form";
import { useReturnStock } from "./ReturnStockLogic";
import { useReturnStockValidation } from "./validation";
import globalStyles from "../../../assets/styles/base/globalPage.module.scss";
import AddCustomerSection from "../StoreCheckout/AddCustomerSection";
import { useMemo } from "react";
import { formatCurrency, formatDiscountInItemName } from "../../../helpers";
import { ExclamationCirlce } from "../../../assets/svgIcons";
import ToolTipV2 from "../ToolTipV2";

const ReturnStockWidget = ({
  closeWidget = () => {},
  defaultOrderId = null,
}) => {
  const {
    formData,
    stores,
    isReturningStock,
    isOrderItemsSelected,
    isLoadingBankAccountsList,
    bankAccounts,
    setFormData,
    handleReturnStock,
  } = useReturnStock({ closeWidget, defaultOrderId });
  const {
    handleSubmit,
    control,
    formState: { errors },
    register,
    trigger,
  } = useReturnStockValidation(formData);

  const totalAmount = useMemo(() => {
    const subtotal =
      formData?.items?.reduce((accumulator, item) => {
        const quantity = item?.isReturning
          ? Number(item?.total_remaining_quantity)
          : 0;
        return accumulator + item.unit_price * quantity;
      }, 0) || 0;

    return subtotal;
  }, [formData?.items, formData?.available_discount]);

  const totalFundableAmount =
    totalAmount - Number(formData?.available_discount);
  const refundableAmount = totalFundableAmount >= 0 ? totalFundableAmount : 0;
  const outstanding = Number(formData?.outstanding_balance || 0);
  const remaining = Number(formData?.total_remaining_credit);
  const remainingDeliveryFee = Number(formData?.remaining_delivery_fee || 0);
  const deliveryFeeRefund = formData?.include_delivery_fee
    ? Number(formData?.refundable_delivery_fee || 0)
    : 0;
  const remainingServiceCharge = Number(
    formData?.remaining_service_charge || 0,
  );
  const serviceChargeRefund = formData?.include_service_charge
    ? Number(formData?.refundable_service_charge || 0)
    : 0;

  const deliveryFeeError = useMemo(() => {
    if (!formData?.include_delivery_fee) return "";
    if (deliveryFeeRefund <= 0) {
      return "Enter a valid delivery fee refund amount";
    }
    if (deliveryFeeRefund > remainingDeliveryFee) {
      return "Amount cannot exceed the remaining delivery fee";
    }
    return "";
  }, [formData?.include_delivery_fee, deliveryFeeRefund, remainingDeliveryFee]);

  const serviceChargeError = useMemo(() => {
    if (!formData?.include_service_charge) return "";
    if (serviceChargeRefund <= 0) {
      return "Enter a valid service charge refund amount";
    }
    if (serviceChargeRefund > remainingServiceCharge) {
      return "Amount cannot exceed the remaining service charge";
    }
    return "";
  }, [
    formData?.include_service_charge,
    serviceChargeRefund,
    remainingServiceCharge,
  ]);

  const refundableWithFees =
    refundableAmount + deliveryFeeRefund + serviceChargeRefund;
  const difference =
    (outstanding || 0) > 0
      ? refundableWithFees - remaining
      : outstanding === 0
        ? refundableWithFees
        : 0;

  return (
    <div className={globalStyles.pageColumnGaps} style={{ minHeight: "100%" }}>
      <form
        onSubmit={handleSubmit(handleReturnStock)}
        id="returnForm"
        className={globalStyles.pageColumnGaps}
      >
        <primaryComponents.SelectFieldV2
          control={control}
          name="store_id"
          options={stores}
          value={
            stores?.find((store) => `${store?.id}` === `${formData?.store_id}`)
              ?.name || "Select store"
          }
          placeholder="Select store"
          label="Select The Store Your Customer Purchased The Product(s) From"
          nameKey={"name"}
          idKey={"id"}
          onChange={(value) => {
            setFormData((prev) => ({
              ...prev,
              store_id: value,
            }));
          }}
          errorMessage={errors.store_id}
        />

        {/* {allow_ledger_account ? (
          <div className="">
            <secondaryComponents.LoaderHelper
              isLoading={isLoadingBankAccountsList}
              className="mt-1"
            >
              <primaryComponents.SelectField
                options={bankAccount?.map((bank) => ({
                  id: bank?.id,
                  name: bank?.account_label,
                }))}
                idKey="id"
                nameKey="name"
                label={"Fund Return From"}
                value={
                  bankAccount?.map(
                    (account) =>
                      `${account?.id}` === `${formData?.ledger_account_id}`,
                  )?.account_label || "Select Bank"
                }
                onChange={(value) => {
                  setFormData((prev) => ({
                    ...prev,
                    ledger_account_id: value,
                  }));
                }}
                floatError={false}
                classNames="bank-name"
                isSearchable={false}
              />
            </secondaryComponents.LoaderHelper>
          </div>
        ) : null} */}

        <Controller
          control={control}
          name="order_id"
          defaultValue={formData.order_id}
          render={({ field: { onChange } }) => (
            <primaryComponents.OrderComboBox
              label="Enter and Search by Order ID"
              defaultOrderId={defaultOrderId}
              isDisabled={!!defaultOrderId}
              onChange={(selectedOrder) => {
                const { id, order_items } = selectedOrder;

                if (selectedOrder) {
                  const orderItems = order_items?.map((order) => ({
                    item_id: order?.id,
                    store_id: formData.store_id,
                    total_remaining_quantity: order?.total_remaining_quantity,
                    quantity: order?.total_remaining_quantity,
                    selling_price: order?.selling_price,
                    unit_price: order?.unit_price,
                    isReturning: false,
                    cart_item_name: order?.cart_item_name,
                  }));

                  const originalDeliveryFee = Number(
                    selectedOrder?.delivery_fee_amount || 0,
                  );
                  const totalReturnedDeliveryFee = Number(
                    selectedOrder?.total_returned_delivery_fee || 0,
                  );
                  const remainingDeliveryFee = Math.max(
                    originalDeliveryFee - totalReturnedDeliveryFee,
                    0,
                  );
                  const originalServiceCharge = Number(
                    selectedOrder?.service_charge || 0,
                  );
                  const totalReturnedServiceCharge = Number(
                    selectedOrder?.total_returned_service_charge || 0,
                  );
                  const remainingServiceCharge = Math.max(
                    originalServiceCharge - totalReturnedServiceCharge,
                    0,
                  );

                  onChange(id);
                  setFormData((prev) => ({
                    ...prev,
                    items: orderItems,
                    order_id: id,
                    available_discount: selectedOrder?.available_discount,
                    total_remaining_credit:
                      selectedOrder?.total_remaining_credit,
                    outstanding_balance:
                      selectedOrder?.customer?.outstanding_balance,
                    include_delivery_fee: false,
                    refundable_delivery_fee: remainingDeliveryFee,
                    delivery_fee_amount: originalDeliveryFee,
                    total_returned_delivery_fee: totalReturnedDeliveryFee,
                    remaining_delivery_fee: remainingDeliveryFee,
                    include_service_charge: false,
                    refundable_service_charge: remainingServiceCharge,
                    service_charge_amount: originalServiceCharge,
                    total_returned_service_charge: totalReturnedServiceCharge,
                    remaining_service_charge: remainingServiceCharge,
                  }));
                }
              }}
              error={errors?.order_id?.message}
            />
          )}
        />

        <div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <p className="dark--text" style={{ fontSize: "14px" }}>
              Select Items Your Customer is Returning
            </p>
            <p className="dark--text" style={{ fontSize: "14px" }}>
              Quantity
            </p>
          </div>
          <div className={styles.returnStockWidgetItems}>
            {formData?.items?.map((item, index) => (
              <div
                key={item?.item_id + index + item?.cart_item_name}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <primaryComponents.Checkbox
                    name="item"
                    id={item.item_id}
                    isChecked={item?.isReturning === true}
                    onChange={(value) => {
                      let orderItems = [...formData.items];
                      orderItems[index].isReturning = value;
                      setFormData((prev) => ({ ...prev, items: orderItems }));
                    }}
                  />
                  <p className="ml-1">
                    {formatDiscountInItemName(item?.cart_item_name)}
                  </p>
                </div>
                <div style={{ width: "72px" }}>
                  <Controller
                    name={`items[${formData?.items?.[index]}]?.total_remaining_quantity`}
                    control={control}
                    defaultValue={
                      formData?.items?.[index]?.total_remaining_quantity
                    }
                    render={({ field: { onChange } }) => {
                      return (
                        <primaryComponents.InputField
                          classNames="white"
                          value={item?.total_remaining_quantity}
                          isDisabled={!item?.isReturning}
                          type="number"
                          step="0.1"
                          min="0.1"
                          name={`items[${formData?.items?.[index]}]?.total_remaining_quantity`}
                          onChange={({ target: { value } }) => {
                            onChange(value);
                            let _orderItems = [...formData.items];
                            _orderItems[index].total_remaining_quantity =
                              Number(value);
                            _orderItems[index].quantity = Number(value);
                            setFormData((prev) => ({
                              ...prev,
                              items: _orderItems,
                            }));
                          }}
                          floatError={false}
                        />
                      );
                    }}
                  />
                </div>
              </div>
            ))}
          </div>

          {formData?.items?.length > 0 && remainingDeliveryFee > 0 ? (
            <div className={styles.deliveryFeeSection}>
              <primaryComponents.Checkbox
                name="include_delivery_fee"
                id="include-delivery-fee"
                isChecked={formData?.include_delivery_fee === true}
                onChange={(value) => {
                  setFormData((prev) => ({
                    ...prev,
                    include_delivery_fee: value,
                    refundable_delivery_fee:
                      value && Number(prev?.refundable_delivery_fee || 0) <= 0
                        ? remainingDeliveryFee
                        : prev?.refundable_delivery_fee,
                  }));
                }}
              >
                <span className="font-weight-semibold">
                  Include Delivery Fee in Return
                </span>
              </primaryComponents.Checkbox>

              <p className="smallest-text-size gray--text mt-1">
                Original delivery fee:{" "}
                {formatCurrency({
                  value: Number(formData?.delivery_fee_amount || 0),
                })}
                {Number(formData?.total_returned_delivery_fee || 0) > 0
                  ? ` · Already refunded: ${formatCurrency({
                      value: Number(formData?.total_returned_delivery_fee || 0),
                    })} · Remaining: ${formatCurrency({
                      value: remainingDeliveryFee,
                    })}`
                  : ""}
              </p>

              <div className="mt-2">
                <primaryComponents.InputField
                  label="Refundable Delivery Fee"
                  name="refundable_delivery_fee"
                  type="number"
                  value={formData?.refundable_delivery_fee}
                  isDisabled={!formData?.include_delivery_fee}
                  min="0.01"
                  max={remainingDeliveryFee}
                  step="0.01"
                  errorMessage={
                    deliveryFeeError ? { message: deliveryFeeError } : undefined
                  }
                  onChange={({ target: { value } }) => {
                    setFormData((prev) => ({
                      ...prev,
                      refundable_delivery_fee: value,
                    }));
                  }}
                />
              </div>
            </div>
          ) : null}

          {formData?.items?.length > 0 && remainingServiceCharge > 0 ? (
            <div className={styles.serviceChargeSection}>
              <primaryComponents.Checkbox
                name="include_service_charge"
                id="include-service-charge"
                isChecked={formData?.include_service_charge === true}
                onChange={(value) => {
                  setFormData((prev) => ({
                    ...prev,
                    include_service_charge: value,
                    refundable_service_charge:
                      value && Number(prev?.refundable_service_charge || 0) <= 0
                        ? remainingServiceCharge
                        : prev?.refundable_service_charge,
                  }));
                }}
              >
                <span className="font-weight-semibold">
                  Include Service Charge in Return
                </span>
              </primaryComponents.Checkbox>

              <p className="smallest-text-size gray--text mt-1">
                Original service charge:{" "}
                {formatCurrency({
                  value: Number(formData?.service_charge_amount || 0),
                })}
                {Number(formData?.total_returned_service_charge || 0) > 0
                  ? ` · Already refunded: ${formatCurrency({
                      value: Number(
                        formData?.total_returned_service_charge || 0,
                      ),
                    })} · Remaining: ${formatCurrency({
                      value: remainingServiceCharge,
                    })}`
                  : ""}
              </p>

              <div className="mt-2">
                <primaryComponents.InputField
                  label="Refundable Service Charge"
                  name="refundable_service_charge"
                  type="number"
                  value={formData?.refundable_service_charge}
                  isDisabled={!formData?.include_service_charge}
                  min="0.01"
                  max={remainingServiceCharge}
                  step="0.01"
                  errorMessage={
                    serviceChargeError
                      ? { message: serviceChargeError }
                      : undefined
                  }
                  onChange={({ target: { value } }) => {
                    setFormData((prev) => ({
                      ...prev,
                      refundable_service_charge: value,
                    }));
                  }}
                />
              </div>
            </div>
          ) : null}

          {formData?.items?.length > 0 ? (
            <>
              <p className="mt-2">
                Subtotal Amount:{" "}
                <span className="dark--text">
                  {formatCurrency({ value: totalAmount })}
                </span>
              </p>
              <p className="mt-2">
                Discount Amount:{" "}
                <span className="dark--text">
                  {formatCurrency({ value: formData?.available_discount })}
                </span>
              </p>
              {formData?.include_service_charge && serviceChargeRefund > 0 ? (
                <p className="mt-2">
                  Service Charge Refund:{" "}
                  <span className="dark--text">
                    {formatCurrency({ value: serviceChargeRefund })}
                  </span>
                </p>
              ) : null}
              {formData?.include_delivery_fee && deliveryFeeRefund > 0 ? (
                <p className="mt-2">
                  Delivery Fee Refund:{" "}
                  <span className="dark--text">
                    {formatCurrency({ value: deliveryFeeRefund })}
                  </span>
                </p>
              ) : null}
              <p className="mt-2">
                Amount Paid:{" "}
                <span className="dark--text">
                  {formatCurrency({ value: refundableAmount })}
                </span>
              </p>
              <p className="mt-2">
                Customer&apos;s Debt:{" "}
                <span className="dark--text">
                  {formatCurrency({ value: outstanding })}
                </span>
              </p>
              <h5 className="mt-3 d-flex align-items-center">
                Total Refundable Amount
                <ToolTipV2 tipText="Total Refundable Amount = Subtotal - discount - customer debt + service charge and delivery fee refunds">
                  <ExclamationCirlce className="mx-2" />:{" "}
                </ToolTipV2>
                <span className="purple--text ml-1">
                  {formatCurrency({
                    value: difference < 0 ? 0 : difference,
                  })}
                </span>
              </h5>
            </>
          ) : null}
        </div>

        <primaryComponents.SelectField
          options={[
            { id: "transfer", name: "Transfer" },
            { id: "card", name: "Card" },
            { id: "cash", name: "Cash" },
            { id: "store_wallet", name: "Store Wallet" },
            { id: "credit_wallet", name: "Credit" },
          ]}
          label={"Refund Method"}
          idKey="id"
          nameKey="name"
          value={formData.payment_method || "Select Payment Method"}
          onChange={(value) => {
            setFormData((prev) => ({
              ...prev,
              payment_method: value,
            }));
          }}
          floatError={false}
          classNames="bank-name"
          isSearchable={false}
        />

        {["transfer", "card", "cash"].includes(formData.payment_method) && (
          <div className="mb-2">
            <secondaryComponents.LoaderHelper
              isLoading={isLoadingBankAccountsList}
              className="mt-1"
            >
              <primaryComponents.SelectFieldV2
                control={control}
                options={bankAccounts?.data?.map((bank) => ({
                  id: bank?.id,
                  name: bank?.account_label,
                }))}
                label={"Fund Returns From"}
                name="ledgerAccount"
                idKey="id"
                nameKey="name"
                value={"Select Bank Account"}
                onChange={(value) => {
                  setFormData((prev) => ({
                    ...prev,
                    ledger_account_id: value,
                  }));
                }}
                floatError={false}
                classNames="bank-name"
                isSearchable={false}
              />
            </secondaryComponents.LoaderHelper>
          </div>
        )}

        <div className="d-flex" style={{ gap: "15px" }}>
          <div style={{ flex: 1 }}>
            <primaryComponents.DateInputV2
              control={control}
              register={register}
              placeholder="Enter return date"
              label={"Return Date"}
              name="return_date"
              value={formData?.return_date}
              onChange={({ target: { value } }) => {
                setFormData((prev) => ({ ...prev, return_date: value }));
              }}
            />
          </div>
          <div style={{ flex: 1 }}>
            <primaryComponents.TimeInputV2
              name="return_time"
              control={control}
              register={register}
              label={"Return Time"}
              value={formData.return_time}
              errorMessage={errors.return_time}
              onChange={({ target: { value } }) => {
                setFormData((prev) => ({ ...prev, return_time: value }));
              }}
              onKeyUp={() =>
                errors.return_time !== undefined && trigger("return_time")
              }
            />
          </div>
        </div>

        <primaryComponents.TextAreaV2
          control={control}
          label="Remarks"
          placeholder="Customer returned this because good was damaged internally"
          value={formData.remarks}
          onChange={({ target: { value } }) => {
            setFormData((prev) => ({ ...prev, remarks: value }));
          }}
          errorMessage={errors.remarks}
        />

        {/* <div>
            <label>Do you want to issue a refund to customer wallet? </label>
            <div className="d-flex" style={{ gap: "35px" }}>
              <primaryComponents.BoxedRadioInput
                style={{ height: "30px" }}
                name="statementFormat"
                value={true}
                id="YES"
                isChecked={formData?.return_to_store_wallet}
                setRadioValue={() => {
                  setFormData((prev) => ({
                    ...prev,
                    return_to_store_wallet: true,
                  }));
                }}
              >
                <span className="font-weight-semibold">YES</span>
              </primaryComponents.BoxedRadioInput>
              <primaryComponents.BoxedRadioInput
                style={{ height: "30px" }}
                name="statementFormat"
                value={false}
                id="NO"
                isChecked={!formData?.return_to_store_wallet}
                setRadioValue={() => {
                  setFormData((prev) => ({
                    ...prev,
                    return_to_store_wallet: false,
                  }));
                }}
              >
                <span className="font-weight-semibold">NO</span>
              </primaryComponents.BoxedRadioInput>
            </div>
          </div> */}

        {formData?.payment_method === "store_wallet" ? (
          <div>
            <p className="font-weight-semibold mb-2">Select Customer </p>
            <AddCustomerSection
              setCustomerDetails={(info) => {
                setFormData((prev) => ({ ...prev, customer: info }));
              }}
              customerDetails={formData?.customer}
              showLoyalty={false}
            />

            <p
              className={`smallest-text-size error-message text-danger ${errors.customer?.message ? "show" : "invisible"}`}
            >
              <FontAwesomeIcon
                icon={["fas", "exclamation-circle"]}
                className="mr-2"
                style={{ fontSize: "13px" }}
              />
              <span>{errors.customer?.message}</span>
            </p>
          </div>
        ) : null}
      </form>

      <primaryComponents.Button
        classNames={"btn btn--primary mt-auto w-100"}
        isDisabled={
          isReturningStock ||
          !isOrderItemsSelected ||
          Boolean(deliveryFeeError) ||
          Boolean(serviceChargeError)
        }
        isLoading={isReturningStock}
        loadingText={"Processing..."}
        type="submit"
        form="returnForm"
      >
        Confirm
      </primaryComponents.Button>
    </div>
  );
};

export default ReturnStockWidget;
